/**
 * Client-side model of a site's layout: its published revisions and, when one
 * is open, its draft. The shapes are the designer's view of the `siteLayout`
 * GraphQL surface (`SiteLayout`, `Zone`, `Entry`, `EntrySide`,
 * `ReaderAssignment`), flattened so the UI can compare and diff revisions
 * without walking nested objects.
 */

export type EntrySideKind = 'ZONE' | 'OUTSIDE';

export type Enforcement = 'ENFORCED' | 'IDENTIFY_ONLY';

export type EntrySidePosition = 'A' | 'B';

export type Zone = {
  id: string;
  name: string;
};

export type EntrySide = {
  id: string;
  position: EntrySidePosition;
  kind: EntrySideKind;
  /** Set when `kind` is `ZONE`; null when the side faces the outside world. */
  zoneId: string | null;
  enforcement: Enforcement;
};

export type Entry = {
  id: string;
  name: string;
  sideA: EntrySide;
  sideB: EntrySide;
};

/** A reader placed on an entry, admitting into one of its sides. */
export type ReaderAssignment = {
  id: string;
  /** The site's reader, which the layout commands name. */
  readerId: string;
  /** The reader device behind it, which the device inventory names. */
  readerDeviceId: string;
  /** The name the layout API gives the reader. */
  readerName: string;
  entryId: string;
  admitsIntoSideId: string;
};

export type Layout = {
  zones: Zone[];
  entries: Entry[];
  readerAssignments: ReaderAssignment[];
};

export type SiteLayoutDraft = Layout & {
  revisionId: string;
};

export type SiteLayoutRevision = Layout & {
  id: string;
  /** Position in the site's history, oldest first from 1. */
  number: number;
  publishedAt: string;
  /** Null while this is the effective revision. */
  closedAt: string | null;
};

export type SiteLayoutState = {
  draft: SiteLayoutDraft | null;
  /** Oldest first; the last one is the effective layout when present. */
  revisions: SiteLayoutRevision[];
};

/** One change to an entry side: what it faces, or how it enforces. */
export type EntrySidePatch =
  | { kind: 'OUTSIDE' }
  | { kind: 'ZONE'; zoneId: string }
  | { enforcement: Enforcement };

export type AssignReaderInput = {
  readerDeviceId: string;
  entryId: string;
  admitsIntoSideId: string;
};

/**
 * What the client refuses before sending a command, and a command it gave up
 * waiting on (`STALLED`). Refusals the server makes arrive as the GraphQL
 * error's own message.
 */
export type SiteLayoutErrorCode =
  | 'NO_ZONES'
  | 'UNKNOWN_READER'
  | 'UNKNOWN_ENTRY'
  | 'UNKNOWN_SIDE'
  | 'NOT_PLACED'
  | 'STALLED';

export class SiteLayoutError extends Error {
  constructor(public readonly code: SiteLayoutErrorCode, message: string) {
    super(message);
    this.name = 'SiteLayoutError';
  }
}
